import { is } from '@common/is/is'

import styles from './Iframe.css'

export type TSrc = (
  `http://localhost:8080${ string }` |
  `https://${ string }` |
  `data:text/html;charset=utf-8,${ string }` |
  `/${ string }`
)

type TSandboxRestriction = (
  'allow-downloads' |
  'allow-downloads-without-user-activation' |
  'allow-forms' |
  'allow-modals' |
  'allow-orientation-lock' |
  'allow-pointer-lock' |
  'allow-popups' |
  // Ez szükséges ahhoz, hogy iframe-ből blank ablakot nyissunk.
  // Több böngésző enélkül is engedi ezt, de pl. Chrome letiltja az oldalt nélküle.
  'allow-popups-to-escape-sandbox' |
  'allow-presentation' |
  // Ez kell ahhoz, hogy használhassuk a cookie-kat, localStorage-et, stb.
  'allow-same-origin' |
  // Ez kell ahhoz, hogy futtasson külső js fájlokat.
  'allow-scripts' |
  // https://developer.mozilla.org/en-US/docs/Web/API/Document/requestStorageAccess
  // Firefox vernyákol, ha nincs bekapcsolva.
  'allow-storage-access-by-user-activation' |
  'allow-top-navigation' |
  'allow-top-navigation-by-user-activation' |
  'allow-top-navigation-to-custom-protocols'
)

type TSandboxRestrictions = readonly TSandboxRestriction[]

type TSize = number | `${ number }%`

interface IProps {
  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#attr-src}
   */
  readonly src?: TSrc

  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#attr-sandbox}
   */
  readonly sandbox: TSandboxRestrictions

  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTTP/Permissions_Policy}
   */
  readonly allow?: unknown

  readonly width?: TSize
  readonly height?: TSize

  /**
   * Teljes html struktúrát adhatunk meg az iframe-nek.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLIFrameElement/srcdoc}
   */
  readonly srcdoc?: string

  readonly className?: string | readonly string[]
}

/**
 * A natív iframe elem fölött egy wrapper komponens a könnyebb és strictebb kezelhetőség végett.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe}
 * @param props - JSX props {@link IProps}.
 */
export default function Iframe (props: IProps): HTMLIFrameElement {
  const cn = [
    styles.iframe,
    is.string(props.className) ? props.className : '',
    ...(is.array(props.className) ? props.className : [])
  ] as const

  const iframe: HTMLIFrameElement = (
    // eslint-disable-next-line react/iframe-missing-sandbox
    <iframe
      className={ cn }
      { ...(is.string(props.allow) ? { allow: props.allow } : {}) }
    />
  )

  if (props.height) {
    iframe.height = `${ props.height }`
  }

  if (props.width) {
    iframe.width = `${ props.width }`
  }

  if (props.src) {
    iframe.src = props.src
  }
  else if (props.srcdoc) {
    iframe.srcdoc = props.srcdoc
  }

  iframe.setAttribute('sandbox', props.sandbox.join(' '))

  return iframe
}
